/**
 * versionUtils.js
 * Central version management — reads and increments app_config/version in Firestore.
 * Full history is preserved in the changelog sub-collection.
 */
import { getApp } from 'firebase/app';
import {
  getFirestore, doc, getDoc, setDoc, addDoc,
  collection, serverTimestamp,
} from 'firebase/firestore';

let _db = null;
const DB = () => {
  if (!_db) { try { _db = getFirestore(getApp()); } catch (_) {} }
  return _db;
};

/**
 * Increment the global app version by 1.
 * Writes to app_config/version AND appends a changelog sub-document.
 *
 * @param {string} reason      - Human-readable reason (e.g. "Portal created: Ground Station")
 * @param {string} updatedBy   - Display name of the user triggering the bump
 * @returns {number|undefined} - New version number, or undefined on failure
 */
export async function bumpVersion(reason = 'Feature update', updatedBy = 'System') {
  const db = DB();
  if (!db) return;
  try {
    const vref = doc(db, 'app_config', 'version');
    const snap = await getDoc(vref);
    const raw = snap.exists() ? snap.data().number : 0;
    const current = parseInt(raw, 10) || 0;
    const next = current + 1;

    // Update the canonical version document
    await setDoc(vref, {
      number:    next,
      changelog: reason,
      updatedAt: serverTimestamp(),
      updatedBy,
    }, { merge: true });

    // Append to the immutable changelog sub-collection
    await addDoc(collection(db, 'app_config', 'version', 'changelog'), {
      version:   next,
      reason,
      updatedAt: serverTimestamp(),
      updatedBy,
    });

    return next;
  } catch (e) {
    // Non-fatal — never block the triggering action
    console.error('[versionUtils] bumpVersion failed:', e);
  }
}
